import styled from 'styled-components';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import AgeGroupIcon from '../../../image/AgeGroupIcon.svg';

// 연령대 index(0=10세미만 ~ 8=80대) 기준 고정 프로그레스 바 색상 //
// 다크: brand500→900 (점점 어둡게), 라이트: brand500→100 (점점 밝게, Figma) //
const PROGRESS_BAR_COLOR_KEYS_DARK = ['brand500', 'brand550', 'brand600', 'brand650', 'brand700', 'brand750', 'brand800', 'brand850', 'brand900'];
const PROGRESS_BAR_COLOR_KEYS_LIGHT = ['brand500', 'brand450', 'brand400', 'brand350', 'brand300', 'brand250', 'brand200', 'brand150', 'brand100'];

// 연령별 이용자 분석 표시 전용 뷰 (집계 완료된 값만 받아 렌더) //
// - 통계: MemberEngagementBox (원본 → 집계) 가 사용 //
// - AI 채팅: EngagementChartRenderer (백엔드 집계 결과) 가 사용 //
export default function MemberEngagementView(props) {
  const {
    ageGroups,            // 프로그레스 바용 9개 연령대(순서 고정) [{ label, totalCount }]
    engagementStates,     // 테이블용 연령대 [{ label, totalCount, reservedSum, usages: [{ membershipName, count }] }]
    engagementTotalCount,
    engagementMaxCount,
    maxLabel,             // "가장 많은 연령" 라벨 (다국어 해석 완료) //
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const progressBarColorKeys = isDark ? PROGRESS_BAR_COLOR_KEYS_DARK : PROGRESS_BAR_COLOR_KEYS_LIGHT;

  // 연령별 비율 계산 //
  function calcPercentage(count) {
    if (engagementTotalCount === 0) return '0';

    return (count / engagementTotalCount * 100).toFixed(1);
  }

  // 프로그레스 바 너비 비율 계산 //
  function calcBarWidth(count) {
    if (engagementMaxCount === 0) return 0;

    return Math.min((count / engagementMaxCount) * 100, 100);
  }

  // 선호 비중 계산 (분모: 해당 연령대의 reserve 누적 합) //
  function calcPreference(usageCount, groupReservedSum) {
    if (groupReservedSum === 0) return '0';

    return (usageCount / groupReservedSum * 100).toFixed(0);
  }

  // 숫자 콤마 포맷팅 //
  function formatNumber(num) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // 프로그레스 바 카드 렌더링 //
  function renderProgressBarCard(group, index) {
    const percentage = calcPercentage(group.totalCount);
    const barWidth = calcBarWidth(group.totalCount);
    const barColor = theme[progressBarColorKeys[index]] || theme.brand500;

    return (
      <ProgressBarCardWrapper key={'ProgressBar-' + index}>
        <ProgressBarInfoRow>
          <ProgressBarLabelWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{group.label}</Text>
            <PercentageWrapper>
              <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.brand500}>{percentage}</Text>
              <PercentageSuffix>
                <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.brand500}>%</Text>
              </PercentageSuffix>
            </PercentageWrapper>
          </ProgressBarLabelWrapper>
          <CountWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>{formatNumber(group.totalCount)}</Text>
            <CountSuffix>
              <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>{t('statistics.engagement.memberUnit')}</Text>
            </CountSuffix>
          </CountWrapper>
        </ProgressBarInfoRow>
        <ProgressBarTrack>
          {
            group.totalCount > 0 &&
            <ProgressBarFill width={barWidth} $barColor={barColor} />
          }
        </ProgressBarTrack>
      </ProgressBarCardWrapper>
    );
  }

  // 테이블 연령 그룹 행 렌더링 //
  function renderAgeGroupRow(group, index) {
    const percentage = calcPercentage(group.totalCount);
    const isLast = index === engagementStates.length - 1;

    return (
      <AgeGroupRow key={'AgeGroup-' + index}>
        <AgeGroupLabelCell $isLast={isLast}>
          <AgePercentCell>
            <PercentageWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.brand500}>{percentage}</Text>
              <PercentageSuffix>
                <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.brand500}>%</Text>
              </PercentageSuffix>
            </PercentageWrapper>
          </AgePercentCell>
          <AgeNameCell>
            <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text}>{group.label}</Text>
          </AgeNameCell>
          <AgeCountCell>
            <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text}>{formatNumber(group.totalCount)}</Text>
            <CountSuffix>
              <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.text}>{t('statistics.engagement.memberUnit')}</Text>
            </CountSuffix>
          </AgeCountCell>
        </AgeGroupLabelCell>
        <MembershipColumnWrapper>
          {
            group.usages.map((usage, usageIndex) => (
              <TableDataCell
                key={'Membership-' + usageIndex}
                $isLast={isLast && usageIndex === group.usages.length - 1}
                $isGroupLast={usageIndex === group.usages.length - 1}>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{usage.membershipName}</Text>
              </TableDataCell>
            ))
          }
        </MembershipColumnWrapper>
        <UsageColumnWrapper>
          {
            group.usages.map((usage, usageIndex) => (
              <TableDataCell
                key={'Usage-' + usageIndex}
                $isLast={isLast && usageIndex === group.usages.length - 1}
                $isGroupLast={usageIndex === group.usages.length - 1}
                $center>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                  {formatNumber(usage.count) + ' ' + t('statistics.countUnit')}
                </Text>
              </TableDataCell>
            ))
          }
        </UsageColumnWrapper>
        <PreferenceColumnWrapper>
          {
            group.usages.map((usage, usageIndex) => (
              <TableDataCell
                key={'Preference-' + usageIndex}
                $isLast={isLast && usageIndex === group.usages.length - 1}
                $isGroupLast={usageIndex === group.usages.length - 1}
                $center>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                  {calcPreference(usage.count, group.reservedSum) + '%'}
                </Text>
              </TableDataCell>
            ))
          }
        </PreferenceColumnWrapper>
      </AgeGroupRow>
    );
  }

  return (
    <>
      <SubtitleRow>
        <AgeRangeImage src={AgeGroupIcon} />
        <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>
          {t('statistics.engagement.mostAge') + ' : ' + maxLabel}
        </Text>
        <PercentageWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.brand500}>
            {engagementTotalCount > 0 ? (engagementMaxCount / engagementTotalCount * 100).toFixed(1) : '0'}
          </Text>
          <PercentageSuffix>
            <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.brand500}>%</Text>
          </PercentageSuffix>
        </PercentageWrapper>
      </SubtitleRow>
      <ProgressBarGridWrapper>
        <ProgressBarRow>
          {ageGroups.slice(0, 3).map((group, index) => renderProgressBarCard(group, index))}
        </ProgressBarRow>
        <ProgressBarRow>
          {ageGroups.slice(3, 6).map((group, index) => renderProgressBarCard(group, index + 3))}
        </ProgressBarRow>
        <ProgressBarRow>
          {ageGroups.slice(6, 9).map((group, index) => renderProgressBarCard(group, index + 6))}
        </ProgressBarRow>
      </ProgressBarGridWrapper>
      {
        engagementStates.length > 0 &&
        <TableSectionWrapper>
          <TableTitleWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {t('statistics.engagement.ageUsageStats')}
            </Text>
          </TableTitleWrapper>
          <TableContentWrapper>
            <TableHeaderRow>
              <TableHeaderCell width={287}>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                  {t('statistics.engagement.colAge')}
                </Text>
              </TableHeaderCell>
              <TableHeaderCell width={350}>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                  {t('statistics.engagement.colMembership')}
                </Text>
              </TableHeaderCell>
              <TableHeaderFlexCell>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                  {t('statistics.engagement.colUsageRecord')}
                </Text>
              </TableHeaderFlexCell>
              <TableHeaderFlexCell>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                  {t('statistics.engagement.colPreference')}
                </Text>
              </TableHeaderFlexCell>
            </TableHeaderRow>
            <TableDividerLine />
            <TableBodyWrapper>
              {engagementStates.map((group, index) => renderAgeGroupRow(group, index))}
            </TableBodyWrapper>
          </TableContentWrapper>
        </TableSectionWrapper>
      }
    </>
  );
}

const SubtitleRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 10px;
`;

const AgeRangeImage = styled.img`
  width: 32px;
  height: 30px;

  margin-top: 5px;
`;

const PercentageWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 1px;
`;

const PercentageSuffix = styled.div`
  display: flex;
  align-items: flex-end;

  padding-bottom: 2px;
`;

const CountWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  gap: 5px;
`;

const CountSuffix = styled.div`
  display: flex;
  align-items: flex-end;

  padding-bottom: 2px;
`;

const ProgressBarGridWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  padding: 20px 0;

  width: 100%;

  gap: 10px;
`;

const ProgressBarRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 40px;
`;

const ProgressBarCardWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  flex: 1;
  min-width: 0;
`;

const ProgressBarInfoRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 10px;
`;

const ProgressBarLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  flex: 1;
  min-width: 0;

  gap: 10px;
`;

const ProgressBarTrack = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 10px;

  border-radius: 1000px;
  background-color: ${props => props.theme.buttonFill3};
`;

const ProgressBarFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;

  width: ${props => props.width}%;
  height: 10px;

  border-radius: 1000px;
  background-color: ${props => props.$barColor};
`;

const TableSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  overflow: hidden;

  width: 100%;

  gap: 10px;
`;

const TableTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;
  overflow: hidden;

  width: 100%;
`;

const TableContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  overflow: hidden;

  width: 100%;

  border-radius: 5px;

  /* 모든 셀의 width에 padding/border 포함 — 헤더·바디 컬럼 폭 일치 //
     (전역 box-sizing reset이 없어 content-box 기본값이라 컬럼이 어긋남) */
  * {
    box-sizing: border-box;
  }
`;

const TableHeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  width: 100%;
  height: 30px;

  border-radius: 5px 5px 0 0;
`;

const TableHeaderCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;

  padding: 0 10px;

  width: ${props => props.width}px;
`;

const TableHeaderFlexCell = styled.div`
  display: flex;
  flex: 1;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  min-width: 0;
`;

const TableDividerLine = styled.div`
  width: 100%;
  height: 1px;

  margin: 7px 0;

  opacity: 0.5;
  background-color: ${props => props.theme.line};
`;

const TableBodyWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  width: 100%;
  max-height: 450px;

  overflow-y: scroll;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.brand500};
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 200px;
  }

  border-radius: 20px;
`;

const AgeGroupRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: stretch;

  width: 100%;
`;

const AgeGroupLabelCell = styled.div`
  display: flex;
  flex-direction: row;

  align-items: stretch;
  overflow: hidden;

  width: 287px;

  background-color: ${props => props.theme.buttonFill};
  border-bottom: ${props => props.$isLast ? 'none' : `1px solid ${props.theme.brand500Alpha50}`};
`;

const AgePercentCell = styled.div`
  display: flex;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  padding: 10px;

  width: 100px;

  flex-shrink: 0;
`;

const AgeNameCell = styled.div`
  display: flex;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  padding: 10px;

  width: 107px;

  flex-shrink: 0;

  border-left: 1px solid ${props => props.theme.brand500};
`;

const AgeCountCell = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  padding: 10px;

  width: 80px;

  flex-shrink: 0;

  border-left: 1px solid ${props => props.theme.brand500};
`;

const MembershipColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  width: 350px;

  border-right: 1px solid ${props => props.theme.line};
`;

const UsageColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  align-items: flex-start;
  justify-content: center;

  min-width: 0;

  border-right: 1px solid ${props => props.theme.line};
`;

const PreferenceColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  align-items: flex-start;
  justify-content: center;

  min-width: 0;
`;

const TableDataCell = styled.div`
  display: flex;
  flex: 1;

  align-items: center;
  justify-content: ${props => props.$center ? 'center' : 'flex-start'};
  overflow: hidden;

  padding: 10px;

  width: 100%;
  min-height: 0;

  border-bottom: ${props => {
    if (props.$isLast) return 'none';
    if (props.$isGroupLast) return `1px solid ${props.theme.brand500Alpha50}`;
    return `1px solid ${props.theme.line}`;
  }};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  cursor: default;
`;
